import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { VARIABLES } from '@environments/environment.variables';
import { CONSOLE_LEVEL_STYLE } from '@shared/services/logs/configs';
import { ConsoleLevel, LogLevel } from '@shared/services/logs/enums';
import { PlatformContextData } from '@shared/services/platform-context';
import { format } from 'date-fns';
import { Observable } from 'rxjs';

/**
 * Centralized client-side logging service with level-based gating and styled console output.
 *
 * @remarks
 * - All methods are no-ops on the server
 * - Supports session-based log level override via sessionStorage
 * - Styled location labels appear when log level is at least INFO
 *
 * @example
 * ```
 * export class ExampleFeature {
 *   private logs = inject(LogsData);
 *
 *   doWork(): void {
 *     this.logs.info('ExampleFeature', 'Starting work');
 *     try {
 *       // ... work
 *       this.logs.debug('ExampleFeature', 'Details', { foo: 'bar' });
 *     } catch (e) {
 *       this.logs.error('ExampleFeature', 'Failed', e);
 *     }
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LogsData {
  private readonly platformContextData = inject(PlatformContextData);

  /**
   * Logs a critical, non-recoverable error when log level is at least FATAL.
   *
   * @param location - Source label (service/component name)
   * @param message - Primary payload to log
   * @param optionalParams - Additional values appended to log
   */
  fatal(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.FATAL) {
      this.logger('error', location, message, ...optionalParams);
    }
  }

  /**
   * Logs an application error when log level is at least ERROR.
   *
   * @param location - Source label
   * @param message - Main content to log
   * @param optionalParams - Additional values
   */
  error(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.ERROR) {
      this.logger('error', location, message, ...optionalParams);
    }
  }

  /**
   * Logs a warning when log level is at least WARN.
   *
   * @param location - Source label
   * @param message - Warning content
   * @param optionalParams - Additional values
   */
  warn(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.WARN) {
      this.logger('warn', location, message, ...optionalParams);
    }
  }

  /**
   * Logs informational message when log level is at least INFO.
   *
   * @param location - Source label
   * @param message - Informational content
   * @param optionalParams - Additional values
   */
  info(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.INFO) {
      this.logger('info', location, message, ...optionalParams);
    }
  }

  /**
   * Logs diagnostic details when log level is at least DEBUG.
   *
   * @param location - Source label
   * @param message - Diagnostic details
   * @param optionalParams - Additional values
   */
  debug(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.DEBUG) {
      this.logger('log', location, message, ...optionalParams);
    }
  }

  /**
   * Logs fine-grained execution trace when log level is at least TRACE.
   *
   * @param location - Source label
   * @param message - Trace note (prefixed with "* ")
   * @param optionalParams - Additional values
   */
  trace(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.TRACE) {
      this.logger('log', location, `* ${String(message)}`, ...optionalParams);
    }
  }

  /**
   * Logs at the most permissive threshold (ALL).
   *
   * @param location - Source label
   * @param message - Content to log
   * @param optionalParams - Additional values
   */
  all(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.ALL) {
      this.logger('log', location, message, ...optionalParams);
    }
  }

  /**
   * Starts a console group when log level is at least the provided level.
   *
   * @param level - Minimum LogLevel required
   * @param isCollapsed - Whether to start collapsed (true) or expanded (false)
   * @param location - Source label displayed as styled prefix
   * @param message - Group title
   * @param optionalParams - Additional values
   *
   * @example
   * ```
   * logs.groupStart(LogLevel.INFO, true, 'ProfilePage', 'Loading profile');
   * logs.info('ProfilePage', 'Fetching preferences');
   * logs.groupEnd(LogLevel.INFO);
   * ```
   */
  groupStart(
    level: LogLevel,
    isCollapsed: boolean,
    location: string,
    message: unknown,
    ...optionalParams: unknown[]
  ): void {
    if (this.platformContextData.isServer()) return;
    if (environment.logLevel >= level) {
      const locationDisplayed = this.getLocationDisplayed('log', location);
      if (typeof console !== 'undefined' && typeof console.groupCollapsed === 'function') {
        if (isCollapsed) {
          console.groupCollapsed(...locationDisplayed, message, ...optionalParams);
        } else {
          console.group(...locationDisplayed, message, ...optionalParams);
        }
      }
    }
  }

  /**
   * Ends the most recently opened console group when log level is at least the provided level.
   *
   * @param level - Minimum LogLevel required
   */
  groupEnd(level: LogLevel): void {
    if (this.platformContextData.isServer()) return;
    if (environment.logLevel >= level) {
      if (typeof console !== 'undefined' && typeof console.groupEnd === 'function') {
        console.groupEnd();
      }
    }
  }

  /**
   * Updates the logging level for the current session from sessionStorage.
   *
   * @returns Observable emitting true if updated, false otherwise
   *
   * @remarks
   * Parses the `LogLevel` key from sessionStorage and updates `environment.logLevel`.
   *
   * @example
   * ```
   * updateLogLevelFromSession().subscribe((success) => {
   *   if (success) console.log('Log level updated');
   * });
   * ```
   */
  updateLogLevelFromSession(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      if (this.platformContextData.isServer()) {
        subscriber.next(false);
        subscriber.complete();
        return;
      }
      try {
        const raw = sessionStorage.getItem('LogLevel');
        if (!raw) {
          subscriber.next(false);
          subscriber.complete();
          return;
        }

        const parsed = Number.parseInt(raw, 10);
        if (Number.isFinite(parsed) && (LogLevel as unknown as Record<number, string>)[parsed]) {
          environment.logLevel = parsed;
          this.internalInfo(
            `📜 Logs display forced to "${(LogLevel as unknown as Record<number, string>)[environment.logLevel]}" for this session.`
          );
          const timestamp = VARIABLES.buildTimestamp;
          if (timestamp > 0) {
            const buildDate = format(new Date(VARIABLES.buildTimestamp), 'dd/MM/yyyy HH:mm');
            console.log(`🗓️ Build date : ${buildDate}`);
          } else {
            console.log('💻 Running in local server environment mode');
          }
          console.log(`🏷️ Environment`, environment);
          subscriber.next(true);
        } else {
          subscriber.next(false);
        }
        subscriber.complete();
      } catch {
        subscriber.error(false);
      }
    });
  }

  /**
   * Logs messages with the specified console level and location.
   *
   * @param level - Console log level
   * @param location - Source label
   * @param message - Primary content
   * @param optionalParams - Additional values
   */
  private readonly logger = (
    level: ConsoleLevel,
    location: string,
    message: unknown,
    ...optionalParams: unknown[]
  ): void => {
    if (this.platformContextData.isServer()) return;
    const locationDisplayed = this.getLocationDisplayed(level, location);

    const fn = (typeof console !== 'undefined' ? console[level] : undefined) as
      | ((...args: unknown[]) => void)
      | undefined;

    if (typeof fn === 'function') {
      fn(...locationDisplayed, message, ...optionalParams);
    }
  };

  /**
   * Computes styled console label for the given location.
   *
   * @param level - Console level for color styling
   * @param location - Source label to prefix
   * @returns Styled label tuple or empty array if log level < INFO
   */
  private readonly getLocationDisplayed = (level: ConsoleLevel, location: string): [string, string] | [] => {
    if (environment.logLevel < LogLevel.INFO) return [];
    const base = this.computeLocationStyles(level);
    return [`%c${location}`, base];
  };

  /**
   * Computes CSS styles for the location label.
   *
   * @param level - Console level for color scheme
   * @returns CSS string with layout and color styles
   */
  private readonly computeLocationStyles = (level: ConsoleLevel): string => {
    const base = 'font-size:8pt;padding:4px 6px;border-radius:4px;border-style:solid;border-width:1px;';
    const color = CONSOLE_LEVEL_STYLE[level] ?? CONSOLE_LEVEL_STYLE.log;
    return `${base}${color}`;
  };

  /**
   * Emits styled informational line for internal service notices.
   *
   * @param message - Text to display
   */
  private readonly internalInfo = (message: string): void => {
    if (typeof console !== 'undefined' && typeof console.info === 'function') {
      console.info(`%c${message}`, 'color:#f06595;font-weight:bold');
    }
  };
}
